/**
 * Cache Service - In-memory caching with TTL for frequently accessed data
 * Reduces database load and improves application performance
 */

class CacheService {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
  }

  /**
   * Generate cache key from parameters
   * @param {string} prefix - Cache key prefix
   * @param {Object} params - Parameters to include in key
   * @returns {string} Cache key
   */
  generateKey(prefix, params = {}) {
    const sortedParams = Object.keys(params)?.sort()?.map(key => `${key}:${JSON.stringify(params?.[key])}`)?.join('|');
    return `${prefix}${sortedParams ? ':' + sortedParams : ''}`;
  }

  /**
   * Get value from cache
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null if expired/not found
   */
  get(key) {
    const item = this.cache?.get(key);
    
    if (!item) {
      return null;
    }

    // Check if expired
    if (Date.now() > item?.expiry) {
      this.cache?.delete(key);
      return null;
    }

    return item?.value;
  }

  /**
   * Set value in cache with TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (optional)
   */
  set(key, value, ttl = this.defaultTTL) {
    const expiry = Date.now() + ttl;
    this.cache?.set(key, { value, expiry });
  }

  /**
   * Delete specific key from cache
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache?.delete(key);
  }

  /**
   * Delete all keys matching a pattern
   * @param {string} pattern - Pattern to match (prefix)
   */
  deletePattern(pattern) {
    const keysToDelete = [];
    
    for (const key of this.cache?.keys()) {
      if (key?.startsWith(pattern)) {
        keysToDelete?.push(key);
      }
    }

    keysToDelete?.forEach(key => this.cache?.delete(key));
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache?.clear();
  }

  /**
   * Get or set pattern - fetch from cache or execute function and cache result
   * @param {string} key - Cache key
   * @param {Function} fetchFn - Async function to fetch data if not cached
   * @param {number} ttl - Time to live in milliseconds (optional)
   * @returns {Promise<any>} Cached or fetched value
   */
  async getOrSet(key, fetchFn, ttl = this.defaultTTL) {
    // Try to get from cache
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }

    // Fetch and cache
    const value = await fetchFn();
    this.set(key, value, ttl);
    return value;
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, item] of this.cache?.entries()) {
      if (now > item?.expiry) {
        keysToDelete?.push(key);
      }
    }

    keysToDelete?.forEach(key => this.cache?.delete(key));
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache stats
   */
  getStats() {
    let expired = 0;
    const now = Date.now();

    for (const item of this.cache?.values()) {
      if (now > item?.expiry) {
        expired++;
      }
    }

    return {
      total: this.cache?.size,
      active: this.cache?.size - expired,
      expired
    };
  }
}

// Create singleton instance
const cacheService = new CacheService();

// Auto cleanup every 10 minutes
setInterval(() => {
  cacheService?.cleanup();
}, 10 * 60 * 1000);

export default cacheService;

// Cache TTL constants for different data types
export const CACHE_TTL = {
  USER_PROFILE: 10 * 60 * 1000,      // 10 minutes
  LISTINGS: 5 * 60 * 1000,           // 5 minutes
  SEARCH_RESULTS: 3 * 60 * 1000,     // 3 minutes
  NOTIFICATIONS: 1 * 60 * 1000,      // 1 minute
  STATIC_DATA: 30 * 60 * 1000,       // 30 minutes (badges, tiers, etc.)
  ANALYTICS: 15 * 60 * 1000,         // 15 minutes
  TRADING_GROUPS: 10 * 60 * 1000,    // 10 minutes
  REVIEWS: 5 * 60 * 1000             // 5 minutes
};